import React, { useState } from "react";
import axios from "axios";

export const AddNotes = ({ onClose }) => {
  const tagOptions = [
    "Enhancement",
    "New Feature",
    "Performance",
    "Security",
    "Bug Fix",
    "UI/UX",
  ];

  const [date, setDate] = useState("");
  const [rows, setRows] = useState(
    Array.from({ length: 4 }, () => ({ tag: "", description: "" }))
  );
  const [loading, setLoading] = useState(false);
  const [existed, setExisted] = useState(false);
  const authToken = localStorage.getItem("authToken");

  const addRow = () => {
    setRows([...rows, { tag: "", description: "" }]);
  };

  const removeRow = (index) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows);
  };

  const handleTagChange = (index, value) => {
    const newRows = [...rows];
    newRows[index].tag = value;
    setRows(newRows);
  };

  const handleDescriptionChange = (index, value) => {
    const newRows = [...rows];
    newRows[index].description = value;
    setRows(newRows);
  };

  const handleSave = async () => {
    if (!date) {
      alert("Please select a date.");
      return;
    }

    const filteredNotes = rows
      .map((row) => ({
        tag: row.tag.trim(),
        description: row.description.trim(),
      }))
      .filter((row) => row.tag && row.description);

    if (filteredNotes.length === 0) {
      alert("Please fill in at least one tag and description.");
      return;
    }

    const payload = {
      date,
      notes: filteredNotes,
    };

    try {
      setLoading(true);
      const BASE = process.env.REACT_APP_BACKEND_URL;
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };
      // Existing date → update (PUT); new date → create (POST).
      const response = existed
        ? await fetch(`${BASE}/API/V2/release-notes/${date}`, {
            method: "PUT",
            headers,
            body: JSON.stringify({ notes: filteredNotes }),
          })
        : await fetch(`${BASE}/API/V2/release-notes`, {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          });

      const result = await response.json();

      if (response.ok) {
        alert("Release notes saved successfully!");
        onClose(); // close modal or drawer
      } else {
        alert(result.error || "Something went wrong.");
      }
    } catch (error) {
      console.error("Error saving release notes:", error);
      alert("An error occurred while saving the release notes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 bg-white rounded-3xl overflow-hidden my-2 w-2/3 h-5/6 pt-6 pr-6">
      <div className="flex items-center justify-center w-full top-6 pl-8">
        <h2 className="text-2xl font-black tracking-tight leading-tight">
          Add note
        </h2>
        <div className="flex items-center justify-center ml-auto text-gray-600 hover:text-gray-900 fill-jiojbm50 p-2 rounded-full hover:bg-jio20">
          <button onClick={onClose}>
            <svg
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
            >
              <path d="M13.41 12l6.3-6.29a1.004 1.004 0 00-1.42-1.42L12 10.59l-6.29-6.3a1.004 1.004 0 10-1.42 1.42l6.3 6.29-6.3 6.29a.999.999 0 000 1.42 1 1 0 001.42 0l6.29-6.3 6.29 6.3a1.001 1.001 0 001.639-.325 1 1 0 00-.22-1.095L13.41 12z"></path>
            </svg>
          </button>
        </div>
      </div>

      <div className="flex flex-col pl-8 gap-2">
        <p className="font-black tracking-tight leading-tight">Select Date</p>
        <input
          type="date"
          id="date"
          value={date}
          onChange={async (e) => {
            const selectedDate = e.target.value;
            setDate(selectedDate);

            try {
              const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/API/V2/release-notes/${selectedDate}`,
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                  },
                }
              );
              const result = res.ok ? await res.json() : null;

              if (result && result.notes) {
                setRows(result.notes);
                setExisted(true);
              } else {
                setRows(
                  Array.from({ length: 4 }, () => ({
                    tag: "",
                    description: "",
                  }))
                );
                setExisted(false);
              }
            } catch (err) {
              console.error("Error fetching notes by date:", err);
              alert("Failed to fetch release notes for selected date.");
            }
          }}
          className="w-1/3 px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-jiojbm50 focus:jiojbm50"
        />
      </div>

      <div className="pl-8 pr-4 overflow-y-auto overflow-thin h-96">
        <table className="min-w-full table-fixed border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2 text-left w-[30%]">Tag</th>
              <th className="border px-4 py-2 text-left w-[60%]">
                Description
              </th>
              <th className="border px-4 py-2 text-center w-[10%]">
                <button
                  onClick={addRow}
                  className="text-green-600 font-bold text-lg"
                >
                  +
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td className="border px-4 py-2 w-[30%]">
                  <select
                    value={row.tag}
                    onChange={(e) => handleTagChange(index, e.target.value)}
                    className="w-full border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-jiojbm50 focus:jiojbm50"
                  >
                    <option value="" disabled>
                      Select a tag
                    </option>
                    {tagOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="border px-4 py-2 w-[60%]">
                  <input
                    type="text"
                    value={row.description}
                    onChange={(e) =>
                      handleDescriptionChange(index, e.target.value)
                    }
                    className="w-full border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-jiojbm50 focus:jiojbm50"
                  />
                </td>
                <td className="border px-4 py-2 text-center w-[10%]">
                  <button
                    onClick={() => removeRow(index)}
                    className="text-red-600 font-bold text-lg"
                  >
                    -
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mb-6 mr-4 ml-auto">
        <button
          className="rounded-3xl px-4 py-2 font-bold focus:outline-none text-white bg-jiojbm50"
          type="button"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};
